// angular
import {Component, OnInit, effect, inject, output} from "@angular/core";
import {Router, RouterModule, NavigationEnd} from "@angular/router";
import {TranslateModule} from "@ngx-translate/core";
import {filter} from "rxjs/operators";
// material angular
import {MatButtonToggleModule} from "@angular/material/button-toggle";
import {MatIconModule} from "@angular/material/icon";
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
} from "@angular/material/expansion";
// components
// services
import {LayoutService} from "../shared/services/layout.service";
import {UserService} from "@app/shared/services/user.service";
import {StorageService} from "@app/shared/services/storage.service";
import {BranchService} from "src/app/shared/services/branch.service";
import {RouteService} from "@app/shared/services/route.service";
// types
import {IUserData} from "@app/shared/types/employee.type";
import {Branch, IBranchInfo} from "@app/shared/types/branch.type";
import {Role} from "@app/shared/types/role.enum";
import {MenuItem} from "../shared/types/menuItem";
// utils
import {SharedModule} from "@app/shared/shared.module";
import {HasAccessDirective} from "@app/core/directives/has-access.directive";
// constants
import {appRoutes} from "@app/shared/constants/routes";
// env
import {environment} from "@environments/environment";

@Component({
  selector: "app-sidebar",
  templateUrl: "./sidebar.component.html",
  styleUrls: ["./sidebar.component.scss"],
  standalone: true,
  imports: [
    SharedModule,
    RouterModule,
    TranslateModule,
    MatIconModule,
    MatButtonToggleModule,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    HasAccessDirective,
  ],
})
export class SidebarComponent implements OnInit {
  public Role = Role;
  public userRoles: string[];

  public environment = environment;
  public branchInfo: IBranchInfo;
  public menuItems: MenuItem[];
  public user: IUserData;
  public defaultBranch: string;
  public userHasBothBranches = false;
  public currentRoute: string = "";
  private _userRoles: Role[];

  readonly clickCloseIcon = output();

  // dependencies
  private _userService = inject(UserService);
  private _branchService = inject(BranchService);
  private _router = inject(Router);
  private _routeService = inject(RouteService);
  private _layoutService = inject(LayoutService);
  private _storageService = inject(StorageService);

  constructor() {
    this.menuItems = this._layoutService.getMenu();
    this.defaultBranch = this._storageService.getBranch();

    // set user roles
    effect(() => {
      this.userRoles = this._userService.userSignal().roles || [];
    });

    effect(() => {
      this._userRoles = this._userService.roles();

      this.userHasBothBranches = !!(
        this._userRoles?.includes(Role.Isfahan) &&
        this._userRoles?.includes(Role.Tehran)
      );
    });

    effect(() => {
      this.user = this._userService.userSignal();
    });

    // Track current route for accordion state
    this._router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.currentRoute = event.urlAfterRedirects || event.url;
      });
  }

  ngOnInit() {
    this._branchService.branchInfo$.subscribe(
      (info) => (this.branchInfo = info)
    );

    // Set initial route
    this.currentRoute = this._router.url;
  }

  public onChangeBranch({value}: {value: Branch}) {
    this._storageService.setBranch(value);
    this._router.navigate(["/"]);
    setTimeout(() => {
      window.location.reload();
    }, 100);
  }

  public toggleMarketLock() {
    this._branchService.updateLock({locked: !this.branchInfo.locked});
  }

  public enableEmergencyMode() {
    this._branchService.enableEmergencyMode();
  }

  public disableEmergencyMode() {
    this._branchService.disableEmergencyMode();
  }

  public clickMenuItem(route: string) {
    const previousUrl = this._routeService.getPreviousUrl();
    // if the destination route is equal to the previous route we will add
    // the previous route query params to reserve the previous page state
    if (
      route !== appRoutes.dashboard &&
      !!previousUrl &&
      previousUrl.split("?")[0] === route
    ) {
      this._router.navigate([previousUrl.split("?")[0]], {
        queryParams: this._generateQueryParams(previousUrl),
        queryParamsHandling: "merge",
      });
    } else {
      this._router.navigate([route]);
    }
    this.clickCloseIcon.emit();
  }

  private _generateQueryParams(url: string) {
    const queryParamsString = url.split("?")[1] || "";
    if (!queryParamsString) {
      return {};
    }
    return queryParamsString
      .split("&")
      .reduce<Record<string, string>>((acc, curr) => {
        acc[curr.split("=")[0]] = curr.split("=")[1];
        return acc;
      }, {});
  }

  public isParentNode(item: MenuItem): boolean {
    return "children" in item && Array.isArray((item as any).children);
  }

  /**
   * Check if accordion should be expanded based on current route
   */

  public isAccordionActive(item: MenuItem): boolean {
    if (!this.isParentNode(item)) {
      return false;
    }

    const parentItem = item as any;
    if (!parentItem.children || !Array.isArray(parentItem.children)) {
      return false;
    }

    // Check if any child route matches the current route
    return parentItem.children.some((child: any) => {
      if (!child.route) return false;

      // Remove query params from current route for comparison
      const currentPath = this.currentRoute.split("?")[0];
      const childRoute = child.route.startsWith("/")
        ? child.route
        : `/${child.route}`;

      if (child.exact) {
        return currentPath === childRoute;
      } else {
        return currentPath.startsWith(childRoute);
      }
    });
  }

  /**
   * Check if parent header should be highlighted
   */
  public isParentHeaderActive(item: MenuItem): boolean {
    return this.isAccordionActive(item);
  }
}
